'use client';

import React from 'react';
import styled from 'styled-components';
import { Modal } from '../Modal/Modal';
import { Button } from '@/components/ui-components/Button/Button';
import { CloseIconRed } from '@/components/icons';
import { theme } from '@/config/theme';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

const ModalHeader = styled.div`
  border-bottom: 1px solid ${theme.colors.gray[200]};
  padding: 14px 20px;
  margin: -24px -24px 20px -24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderAccent = styled.div`
  width: 5px;
  height: 18px;
  background-color: ${theme.colors.primary.accent};
  border-radius: 2px;
  flex-shrink: 0;
`;

const HeaderText = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.gray[900]};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  width: 24px;
  height: 24px;
  line-height: 0;
  transition: all 0.2s ease;
  border-radius: 4px;
  flex-shrink: 0;
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  &:hover {
    background: ${theme.colors.gray[100]};
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Message = styled.p`
  color: #4b5563;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

/**
 * ConfirmationModal Component
 * 
 * A reusable confirmation dialog for user actions
 * 
 * @component
 * @example
 * ```tsx
 * <ConfirmationModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Configuration"
 *   message="Are you sure you want to delete this configuration? This action cannot be undone."
 *   variant="danger"
 *   confirmText="Delete"
 *   cancelText="Cancel"
 * />
 * ```
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
}) => {
  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      console.error('onConfirm error:', error);
      throw error;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="480px"
      backdropBlur
    >
      <ModalHeader>
        <HeaderLeft>
          <HeaderAccent />
          <HeaderText>{title}</HeaderText>
        </HeaderLeft>
        <CloseButton onClick={onClose} aria-label="Close">
          <CloseIconRed />
        </CloseButton>
      </ModalHeader>
      <ModalBody>
        <Message>{message}</Message>
        <ButtonGroup>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
            type="button"
          >
            {cancelText}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isLoading}
            type="button"
          >
            {isLoading ? 'Processing...' : confirmText}
          </Button>
        </ButtonGroup>
      </ModalBody>
    </Modal>
  );
};

export default ConfirmationModal;

